import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, ArrowRight, Clock, CheckCircle, Calendar, 
  MapPin, MessageSquare, Filter, Search 
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import fileTextIcon from '../../assets/img3d/contrat.png';
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyRequestsPage = () => {
  const navigate = useNavigate();
  const [allRequests, setAllRequests] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [hydrating, setHydrating] = useState(false);

  const getPropertyId = (request = {}) =>
    request.property?.id ||
    request.propertyId ||
    request.property_id ||
    request.property?.property_id ||
    null;
  const getPropertyImage = (property = {}, request = {}) => {
    const firstImage = property.images?.[0];
    const firstImageUrl = typeof firstImage === 'string'
      ? firstImage
      : firstImage?.url || firstImage?.secure_url || firstImage?.src;

    return (
      firstImageUrl ||
      property.coverImage ||
      property.thumbnail ||
      property.image ||
      property.image_url ||
      property.imageUrl ||
      property.property_image ||
      property.property_image_url ||
      property.main_image ||
      property.mainImage ||
      request.propertyImage ||
      request.property_image ||
      request.property_image_url ||
      ''
    );
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/my-requests`);
      const requestsData = response.data?.items || response.data || [];
      const safeList = Array.isArray(requestsData) ? requestsData : [];
      setAllRequests(safeList);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setAllRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const normalizeStatus = (status) => (status || '').toLowerCase().replace(/_/g, '_');

  useEffect(() => {
    if (filter === 'all') {
      setRequests(allRequests);
    } else {
      setRequests(allRequests.filter((r) => normalizeStatus(r.status) === filter));
    }
  }, [filter, allRequests]);

  useEffect(() => {
    const hydrate = async () => {
      const missing = allRequests.filter((r) => {
        const property = r.property || {};
        const hasImage = !!getPropertyImage(property, r);
        const propId = getPropertyId(r);
        return !hasImage && propId;
      });

      if (missing.length === 0) return;
      setHydrating(true);

      const updates = await Promise.all(
        missing.map(async (r) => {
          const propId = getPropertyId(r);
          if (!propId) return null;
          try {
            const res = await axios.get(`${API}/properties/${propId}`);
            const propData = res.data?.property || res.data || {};
            return { requestId: r.id, property: propData };
          } catch (e) {
            console.error('Error fetching property for request', r.id, e);
            return null;
          }
        })
      );

      setAllRequests((prev) =>
        prev.map((r) => {
          const update = updates.find((u) => u && u.requestId === r.id);
          return update ? { ...r, property: { ...update.property } } : r;
        })
      );

      setHydrating(false);
    };

    hydrate();
  }, [allRequests]);

  // Configuration des statuts (couleurs et icônes)
  const getStatusConfig = (status) => {
    const normalizedStatus = (status || '').toLowerCase().replace(/_/g, '_');
    const configs = {
      new: { color: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Nouveau', icon: Clock },
      qualified: { color: 'bg-indigo-50 text-indigo-700 border-indigo-200', label: 'En cours', icon: Clock },
      assigned: { color: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Assigné', icon: Clock },
      in_process: { color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'En traitement', icon: Clock },
      visit_planned: { color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Visite', icon: Calendar },
      completed: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Terminé', icon: CheckCircle },
      lost: { color: 'bg-gray-50 text-gray-600 border-gray-200', label: 'Clôturé', icon: CheckCircle }
    };
    return configs[normalizedStatus] || configs.new;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { 
      day: 'numeric', month: 'short'
    });
  };

  const handleConfirmVisit = async (request) => {
    const visitDate =
      request.proposed_visit_date || request.proposedVisitDate || request.visit_date || request.visitDate;
    const body = {
      visitDate,
      visitSlotId: request.proposed_visit_slot_id || request.proposedVisitSlotId || request.visit_slot_id || request.visitSlotId,
    };
    try {
      await axios.post(`${API}/client/my-requests/${request.id}/confirm-visit`, body);
      toast.success?.('Visite confirmée'); // if toast hook exposes success
    } catch (error) {
      console.error('Error confirming visit:', error);
    } finally {
      fetchRequests();
    }
  };

  const filtersList = [
    { id: 'all', label: 'Toutes' },
    { id: 'new', label: 'Nouveau' },
    { id: 'qualified', label: 'En cours' },
    { id: 'assigned', label: 'Assigné' },
    { id: 'in_process', label: 'En traitement' },
    { id: 'visit_planned', label: 'Visites' },
    { id: 'completed', label: 'Terminées' },
    { id: 'lost', label: 'Clôturées' }
  ];

  return (
    <Layout
      headerConfig={{
        icon: fileTextIcon,
        iconColor: 'bg-white',
        title: 'Mes Demandes',
        subtitle: 'Suivez l\'avancement de vos projets immobiliers',
        primaryAction: 'Nouvelle Recherche',
        onPrimaryAction: () => navigate('/properties'),
      }}
    >
      <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-500">
        
        {/* --- FILTRES CHIPS --- */}
        <div className="flex flex-wrap items-center gap-3 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500 mr-2">
            <Filter className="h-4 w-4" />
            <span>Filtres :</span>
          </div>
          {filtersList.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border",
                filter === f.id
                  ? "bg-[#FF9F68] text-white border-[#FF9F68] shadow-sm shadow-orange-100"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* --- LISTE DES DEMANDES --- */}
        {loading ? (
          <div className="grid grid-cols-1 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-gray-50 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
              <Search className="h-8 w-8 text-gray-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Aucune demande trouvée</h3>
            <p className="text-gray-500 mb-6">Vous n'avez pas encore de demande correspondant à ce filtre.</p>
            <Button 
              onClick={() => navigate('/properties')}
              className="bg-[#FF9F68] hover:bg-[#E67E4F]"
            >
              Lancer une recherche
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {requests.map((request) => {
              const status = getStatusConfig(request.status);
              const StatusIcon = status.icon;
              const property = request.property || {};
              const propertyTitle = property.title || request.propertyTitle || 'Recherche personnalisée';
              const propertyImage = getPropertyImage(property, request);
              const pendingClient = (request.visit_status || '').toLowerCase() === 'pending_client';
              const proposedDate = request.proposed_visit_date || request.proposedVisitDate;

              return (
                <Card 
                  key={request.id} 
                  className="group overflow-hidden rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(`/client/my-requests/${request.id}`)}
                >
                  <div className="flex flex-col md:flex-row">
                    
                    {/* Vignette Image (Gauche) */}
                    <div className="w-full md:w-48 h-32 md:h-auto bg-gray-100 flex-shrink-0 relative overflow-hidden">
                      {propertyImage ? (
                        <img 
                          src={propertyImage} 
                          alt="Bien" 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-50">
                          <FileText className="h-8 w-8 text-gray-300" />
                        </div>
                      )}
                      {/* Badge Date sur Mobile (Overlay) */}
                      <div className="absolute top-2 left-2 md:hidden">
                         <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm text-xs shadow-sm">
                           {formatDate(request.createdAt || request.created_at)}
                         </Badge>
                      </div>
                    </div>

                    {/* Contenu (Droite) */}
                    <div className="flex-1 p-6 flex flex-col justify-between">
                      <div className="flex justify-between items-start gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                          <Badge variant="outline" className={cn("border px-2 py-0.5 text-xs font-medium flex items-center gap-1.5", status.color)}>
                              <StatusIcon className="h-3 w-3" />
                              {status.label}
                            </Badge>
                            <span className="text-xs text-gray-400 hidden md:inline-block">
                              • Créée le {formatDate(request.createdAt || request.created_at)}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#FF9F68] transition-colors">
                            {propertyTitle}
                          </h3>
                          {property.city && (
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {property.city}
                            </p>
                          )}
                        </div>
                        
                        <div className="hidden md:block">
                           <Button 
                            variant="ghost" 
                            size="icon" 
                            className="rounded-full hover:bg-orange-50 text-gray-400 hover:text-[#FF9F68]"
                          >
                            <ArrowRight className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>

                      {/* Extrait du message & Footer */}
                      <div className="mt-1 pt-2 border-t border-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1">
                           <p className="text-sm text-gray-600 line-clamp-1 italic">
                             "{request.seekerMessage || request.seeker_message || "Aucun message"}"
                           </p>
                        </div>
                        
                        {/* Indicateurs (Visite / Messages) */}
                        <div className="flex items-center gap-4 text-xs font-medium text-gray-500">
                          {(request.visitScheduled || request.visit_scheduled || request.visit_status === 'PENDING_CLIENT') && (
                            <div className="flex items-center gap-1.5 text-yellow-600 bg-yellow-50 px-2 py-1 rounded-md">
                              <Calendar className="h-3.5 w-3.5" />
                              <span>{pendingClient ? 'Créneau à confirmer' : 'Visite prévue'}</span>
                            </div>
                          )}
                          {(request.messages?.length > 0) && (
                            <div className="flex items-center gap-1.5 text-orange-600 bg-orange-50 px-2 py-1 rounded-md">
                              <MessageSquare className="h-3.5 w-3.5" />
                              <span>{request.messages.length} msg</span>
                            </div>
                          )}
                        </div>

                        {pendingClient && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              className="bg-[#FF9F68] hover:bg-[#E67E4F] text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConfirmVisit(request);
                              }}
                            >
                              Confirmer ce créneau
                            </Button>
                            {proposedDate && (
                              <span className="text-xs text-gray-500">
                                {formatDate(proposedDate)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyRequestsPage;